import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onIdTokenChanged, signOut as fbSignOut, type User } from 'firebase/auth';
import { normalizeEmail, roleFromClaims, type StaffRole } from '@brands/shared';
import { firebase } from '../lib/firebase';

export interface Session {
  user: User;
  email: string;
  role: StaffRole | null;
  /** Admin o editor: ve el panel completo. */
  isStaff: boolean;
  /** Solo admin: además administra roles y las cuentas del equipo. */
  isAdmin: boolean;
}

interface AuthState {
  /** `undefined` mientras Firebase resuelve la sesión inicial. */
  session: Session | null | undefined;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    const { auth } = firebase();
    // onIdTokenChanged también se dispara al renovar el token, así un cambio de rol se refleja
    // sin recargar la página.
    return onIdTokenChanged(auth, async (user) => {
      if (!user?.email) {
        setSession(null);
        return;
      }
      const token = await user.getIdTokenResult();
      setSession({
        user,
        email: normalizeEmail(user.email),
        role: roleFromClaims(token.claims),
        isStaff: roleFromClaims(token.claims) !== null,
        isAdmin: roleFromClaims(token.claims) === 'admin',
      });
    });
  }, []);

  // Al volver a la pestaña se renueva el token (máx. cada 5 min) para que un cambio de rol
  // hecho por un admin se aplique sin cerrar sesión.
  useEffect(() => {
    let last = Date.now();
    const onFocus = () => {
      const user = firebase().auth.currentUser;
      if (!user || Date.now() - last < 5 * 60_000) return;
      last = Date.now();
      void user.getIdToken(true).catch(() => undefined);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  const value = useMemo<AuthState>(
    () => ({ session, signOut: () => fbSignOut(firebase().auth) }),
    [session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
